import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const empty = { name: '', firma: '', email: '', telefon: '', anlagengroesse: '', nachricht: '' };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail?.[0]?.msg || 'Senden fehlgeschlagen');
      }
      setSent(true);
      toast.success('Anfrage gesendet. Wir melden uns innerhalb von 24h.');
      setForm(empty);
    } catch (err) {
      toast.error(err.message || 'Fehler beim Senden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="py-16 sm:py-24 md:py-32 relative border-t border-slate-200/70" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-5">Kontakt</div>
          <h2 className="font-display font-black text-slate-900 text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-[1.02] mb-6">
            Audit<br />
            <span className="text-[#0891b2]">anfragen.</span>
          </h2>
          <p className="text-slate-600 text-base mb-10 max-w-md">
            Unverbindlich. Im laufenden Betrieb. Mit konkreter Aussage zu Ihrem Einsparpotenzial in Euro.
          </p>

          <div className="space-y-5 text-sm">
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 border border-[#0891b2]/30 grid place-items-center shrink-0">
                <Mail className="w-4 h-4 text-[#0891b2]" />
              </div>
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">E-Mail</div>
                <a href="mailto:M.Basic@mibax-messtechnik.com" className="text-slate-900 hover:text-[#0891b2] break-all" data-testid="contact-email">
                  M.Basic@mibax-messtechnik.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 border border-[#0891b2]/30 grid place-items-center shrink-0">
                <Phone className="w-4 h-4 text-[#0891b2]" />
              </div>
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Telefon</div>
                <a href="tel:+436769218022" className="text-slate-900 hover:text-[#0891b2]">
                  +43 676 9218022
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 border border-[#0891b2]/30 grid place-items-center shrink-0">
                <MapPin className="w-4 h-4 text-[#0891b2]" />
              </div>
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Einsatzgebiet</div>
                <div className="text-slate-900">Deutschland · Österreich · Schweiz</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="card-surface p-6 sm:p-8 md:p-10" data-testid="contact-form">
            {sent ? (
              <div className="text-center py-12" data-testid="contact-success">
                <CheckCircle2 className="w-12 h-12 text-[#00ff66] mx-auto mb-5" />
                <div className="font-display font-black text-slate-900 text-2xl tracking-tight mb-2">Vielen Dank.</div>
                <div className="text-slate-600 max-w-md mx-auto">
                  Ihre Anfrage ist bei uns eingegangen. Ein MIBAX-Berater meldet sich innerhalb von 24 Stunden 
                  bei Ihnen, um den Audit-Termin abzustimmen.
                </div>
                <button onClick={() => setSent(false)} className="btn-secondary mt-7" type="button">
                  Weitere Anfrage stellen
                </button>
              </div>
            ) : (
              <>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-8">
                  Anfrageformular · Antwort innerhalb 24h
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Input label="Name *" name="name" value={form.name} onChange={change('name')} required testId="input-name" />
                  <Input label="Firma *" name="firma" value={form.firma} onChange={change('firma')} required testId="input-firma" />
                  <Input label="E-Mail *" name="email" type="email" value={form.email} onChange={change('email')} required testId="input-email" />
                  <Input label="Telefon" name="telefon" value={form.telefon} onChange={change('telefon')} testId="input-telefon" />
                  <div className="sm:col-span-2">
                    <Input label="Anlagengröße / Kompressorleistung" name="anlagengroesse" value={form.anlagengroesse} onChange={change('anlagengroesse')} placeholder="z. B. 75 kW Kompressor, 6 bar" testId="input-anlagengroesse" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                      Nachricht
                    </label>
                    <textarea
                      value={form.nachricht}
                      onChange={change('nachricht')}
                      rows={4}
                      placeholder="Erzählen Sie uns kurz von Ihrer Anlage und Ihrem Anliegen…"
                      className="w-full px-4 py-3 rounded-sm text-sm"
                      data-testid="input-nachricht"
                    />
                  </div>
                </div>

                <button type="submit" disabled={loading} className="btn-primary mt-8 w-full sm:w-auto" data-testid="contact-submit-button">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sende…</> : <>Audit anfragen <Send className="w-4 h-4" /></>}
                </button>

                <div className="mt-5 text-xs text-slate-500">
                  Mit Absenden stimmen Sie unserer Datenschutzerklärung zu.
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Input({ label, testId, ...props }) {
  return (
    <div>
      <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">
        {label}
      </label>
      <input
        {...props}
        data-testid={testId}
        className="w-full px-4 py-3 rounded-sm text-sm"
      />
    </div>
  );
}
